// 05b · Prélèvements SCO — le comptage du vidage des automates.
//
// Trois blocs, dans l'ordre où l'argent sort de la machine : les pièces (un
// total, pas de détail — c'est ce que le poste compte), les billets restés en
// vrac, et les enveloppes scellées de 500 €.
//
// La règle des 500 € est DURE. Une composition libre qui ne tombe pas juste est
// refusée, et l'écran dit de combien : « il manque 60,00 € » se corrige,
// « composition invalide » se subit.
//
// Ce que produit cet écran ne s'arrête pas ici :
//   • les ENVELOPPES traversent vers le coffre (étape 10). Scellées, elles ne
//     seront pas recomptées mais elles y sont bien.
//   • les pièces et le vrac ne traversent PAS : Kamel les mêle au coffre, donc
//     le comptage de l'étape 10 les prend déjà. Les faire traverser les
//     doublerait — c'est l'erreur que ce fichier existe pour ne pas commettre.
//
// Le rapprochement ORIKA est une SAISIE, pas un contrôle : ces montants
// concernent la journée en cours, dont les fichiers ORIKA n'existent pas encore.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BILLET_KEY, J1_PRESETS } from 'sotcp-core/inventaireCoffreCalc'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { VALEUR_ENVELOPPE } from '@/domain/derive'
import { couleurEcart, fmtEcart, lampeEcart, motEcart } from '@/domain/ecart'
import {
  COUPURES, compositionsStandard, lirePrelevSco, totalDetail, totauxSco, verifierEnveloppe,
  type LigneCoupure, type PrelevSco,
} from '@/domain/especes'
import { fmtEur, pluriel } from '@/lib/format'
import { Card, Chip, Kpis, Modale, Notice, Vide } from '@/ui/primitives'
import type { Rapport } from '@/domain/types'

/** Le geste est fini quand les doigts s'arrêtent : au-delà, l'écriture part. */
const DELAI = 400

const uid = () => `env_${Math.random().toString(36).slice(2, 10)}`

export default function PrelevementsSco({ rapport, lectureSeule }: {
  rapport: Rapport | null
  lectureSeule: boolean
}) {
  const { date, ecrireRapport } = useRun()

  const enregistre = useMemo(() => lirePrelevSco(rapport), [rapport])

  // Le brouillon tient la frappe en local — les totaux bougent sous les doigts —
  // et l'écriture part une fois, à la fin. Sans lui, servir onze coupures
  // faisait onze allers-retours et autant de recalculs de coffre.
  const [brouillon, setBrouillon] = useState<PrelevSco | null>(null)
  const courant: PrelevSco = brouillon ?? enregistre ?? { pieces: 0, billets: {}, enveloppes: [], orika: null }
  const [composer, setComposer] = useState(false)

  const tempo = useRef<number | undefined>(undefined)
  const vivant = useRef(true)
  useEffect(() => () => { vivant.current = false; window.clearTimeout(tempo.current) }, [])

  const envoyer = useCallback(async (p: PrelevSco) => {
    try {
      await ecrireRapport('prélèvements SCO', { prelevSco: p },
        // Le total du vidage et son entrée de coffre doivent bouger ENSEMBLE :
        // le serveur repose la ligne dans la foulée de l'écriture. Laisser
        // l'écran de coffre s'en charger plus tard ouvrirait une fenêtre où le
        // comptage voit un vidage que les entrées ignorent.
        () => api.patcherRapport(date, { prelevSco: p })
          .then(() => api.synchroniserEntreeSco(date)),
        '06')
      if (vivant.current) setBrouillon(null)
    } catch {
      // ecrireRapport a déjà remis l'état d'avant et dit pourquoi. Le brouillon
      // reste : la frappe n'est pas perdue, elle attend une seconde tentative.
    }
  }, [date, ecrireRapport])

  const poser = useCallback((calcul: (p: PrelevSco) => PrelevSco, tout_de_suite = false) => {
    if (lectureSeule) return
    setBrouillon((b) => {
      const suite = calcul(b ?? enregistre ?? { pieces: 0, billets: {}, enveloppes: [], orika: null })
      window.clearTimeout(tempo.current)
      if (tout_de_suite) void envoyer(suite)
      else tempo.current = window.setTimeout(() => { void envoyer(suite) }, DELAI)
      return suite
    })
  }, [lectureSeule, enregistre, envoyer])

  const t = totauxSco(courant)
  const standards = useMemo(() => compositionsStandard(J1_PRESETS), [])

  // Une enveloppe libre déjà posée peut avoir été saisie ailleurs : on la
  // signale plutôt que de la laisser fausser le total en silence.
  const invalides = courant.enveloppes.filter((e) => !verifierEnveloppe(e.detail).ok)

  return (
    <>
      <Kpis items={[
        { k: 'Pièces', v: fmtEur(t.pieces) },
        { k: 'Billets en vrac', v: fmtEur(t.billets) },
        {
          k: 'Enveloppes', v: `${courant.enveloppes.length} × ${fmtEur(VALEUR_ENVELOPPE)}`,
          s: fmtEur(t.enveloppes), couleur: courant.enveloppes.length > 0 ? 'var(--act)' : undefined,
        },
        { k: 'Total compté', v: fmtEur(t.total) },
      ]} />

      {invalides.length > 0 && (
        <Notice c="stop">
          <b>{pluriel(invalides.length, 'enveloppe ne vaut pas 500 €', 'enveloppes ne valent pas 500 €')}</b> —
          {' '}une enveloppe de prélèvement vaut toujours {fmtEur(VALEUR_ENVELOPPE)}. Corrige-la ou
          retire-la : sans quoi le coffre recevra un montant que personne n’a mis dedans.
        </Notice>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 12 }}>
        <Card titre="Pièces" actions={<span className="mo">{fmtEur(t.pieces)}</span>}>
          <div className="slot" style={{ borderBottom: 'none' }}>
            <span className="nm">Total des pièces</span>
            <span className="sp" />
            <Montant valeur={courant.pieces} fige={lectureSeule} largeur={130}
                     etiquette="Total des pièces du vidage"
                     changer={(v) => poser((p) => ({ ...p, pieces: v }))} />
          </div>
          <div className="hint" style={{ padding: '8px 12px' }}>
            Un total unique — pas de détail par dénomination. Ces pièces sont mêlées au coffre :
            elles seront saisies par le comptage de l’étape 10, et ne traversent donc pas d’ici.
          </div>
        </Card>

        <Card titre="Billets en vrac" actions={<span className="mo">{fmtEur(t.billets)}</span>}>
          <table className="t">
            <tbody>
              {COUPURES.map((d) => {
                const cle = BILLET_KEY(d)
                const n = Number(courant.billets[cle]) || 0
                return (
                  <tr key={cle}>
                    <td><span className="mo">{d} €</span></td>
                    <td style={{ width: 96 }}>
                      <Montant valeur={n} fige={lectureSeule} largeur={80} entier
                               etiquette={`Nombre de billets de ${d} €`}
                               changer={(v) => poser((p) => ({
                                 ...p, billets: { ...p.billets, [cle]: v },
                               }))} />
                    </td>
                    <td className="n" style={{ color: n > 0 ? 'var(--ink)' : 'var(--ink4)' }}>
                      {fmtEur(n * d)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="hint" style={{ padding: '8px 12px' }}>
            Comme les pièces : mêlés au coffre, donc comptés à l’étape 10. Ils ne traversent pas.
          </div>
        </Card>
      </div>

      <Card
        titre="Enveloppes préparées"
        lampe={invalides.length > 0 ? 'stop' : courant.enveloppes.length > 0 ? 'ok' : undefined}
        style={{ marginTop: 12 }}
        actions={<>
          <Chip c="act">TRAVERSE VERS LE COFFRE</Chip>
          <span className="mo">{fmtEur(t.enveloppes)}</span>
        </>}
      >
        {!lectureSeule && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '9px 12px', borderBottom: '1px solid var(--line)' }}>
            {standards.map((c) => (
              <button className="btn sm" key={c.label}
                      onClick={() => poser((p) => ({
                        ...p,
                        // Le détail est RECOPIÉ : c'est une constante du paquet
                        // partagé, la poser telle quelle ferait tenir deux
                        // enveloppes par le même objet.
                        enveloppes: [...p.enveloppes, {
                          id: uid(), libre: false,
                          detail: c.detail.map((l) => ({ ...l })),
                        }],
                      }), true)}>
                {c.label}
              </button>
            ))}
            <button className="btn act sm" onClick={() => setComposer(true)}>Composition libre…</button>
          </div>
        )}

        {courant.enveloppes.length === 0
          ? <Vide>Aucune enveloppe préparée. Les billets scellés se déclarent ici ; le vrac reste au bloc du dessus.</Vide>
          : courant.enveloppes.map((e, i) => {
            const v = verifierEnveloppe(e.detail)
            return (
              <div className="slot" key={e.id}>
                <Chip c={e.libre ? 'act' : 'neutre'}>{e.libre ? 'LIBRE' : 'STANDARD'}</Chip>
                <span className="nm">Enveloppe {i + 1}</span>
                <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
                  {e.detail.filter((l) => l.quantite > 0)
                    .map((l) => `${l.quantite} × ${l.denomination} €`).join(' + ') || '—'}
                </span>
                <span className="sp" />
                {v.ok
                  ? <span className="mo">{fmtEur(v.total)}</span>
                  : <Chip c="stop">{v.message.toUpperCase()}</Chip>}
                {!lectureSeule && (
                  <button className="btn stop sm" aria-label={`Retirer l’enveloppe ${i + 1}`}
                          onClick={() => poser((p) => ({
                            ...p, enveloppes: p.enveloppes.filter((x) => x.id !== e.id),
                          }), true)}>✕</button>
                )}
              </div>
            )
          })}
      </Card>

      <Notice c="act">
        <b>Ce total part au coffre.</b> Les {fmtEur(t.total)} du vidage s’inscrivent tout seuls en
        entrée de coffre à l’étape 10 : c’est de l’argent qui y entre. Le solde attendu monte donc
        d’autant que le comptage — l’écart de coffre, lui, ne bouge pas.
      </Notice>

      <Card titre="Rapprochement ORIKA" style={{ marginTop: 12 }}
            lampe={!t.orikaSaisi ? 'warn' : lampeEcart(t.ecartOrika)}>
        <div className="slot">
          <span className="nm">Montant annoncé par ORIKA</span>
          <span className="sp" />
          <Montant valeur={courant.orika} fige={lectureSeule} largeur={140} vide
                   etiquette="Montant annoncé par ORIKA"
                   changer={(v) => poser((p) => ({ ...p, orika: v }))} />
        </div>
        <div className="slot">
          <span className="nm">Notre total</span>
          <span className="sp" />
          <span className="mo">{fmtEur(t.total)}</span>
        </div>
        <div className="slot" style={{ borderBottom: 'none' }}>
          <span className="nm">Écart</span>
          <span className="sp" />
          {t.orikaSaisi
            ? <span className="mo" style={{ fontSize: 15, color: couleurEcart(t.ecartOrika) }}
                    title={motEcart(t.ecartOrika)}>
                {fmtEcart(t.ecartOrika)}
              </span>
            : <span className="hint">rien à comparer tant qu’ORIKA n’est pas saisi</span>}
        </div>
        <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
          Aucune vérification automatique n’est possible le jour même : ces montants concernent la
          journée en cours, dont les fichiers ORIKA n’existent pas encore. Le rapprochement est donc
          une saisie — l’écart est conservé et remonte au bilan.
        </div>
      </Card>

      {composer && (
        <ModaleComposition
          fermer={() => setComposer(false)}
          valider={(detail) => {
            poser((p) => ({
              ...p, enveloppes: [...p.enveloppes, { id: uid(), libre: true, detail }],
            }), true)
            setComposer(false)
          }}
        />
      )}
    </>
  )
}

// ── La composition libre ──────────────────────────────────────────────────

/**
 * Le contrôle des 500 € se fait ICI, à la porte : une enveloppe fausse n'entre
 * pas. Le bouton de validation reste inerte tant que le compte ne tombe pas, et
 * le manque ou l'excédent est affiché en continu pendant qu'on tape — corriger
 * en voyant l'écart fondre vaut mieux qu'un refus à la fin.
 */
function ModaleComposition({ fermer, valider }: {
  fermer: () => void
  valider: (detail: LigneCoupure[]) => void
}) {
  const [q, setQ] = useState<Record<string, number>>({})
  const detail: LigneCoupure[] = COUPURES
    .map((d) => ({ denomination: d, quantite: Number(q[BILLET_KEY(d)]) || 0 }))
    .filter((l) => l.quantite > 0)
  const v = verifierEnveloppe(detail)
  const total = totalDetail(detail)

  return (
    <Modale titre="Composition libre" fermer={fermer}
            sous={`Une enveloppe de prélèvement vaut toujours ${fmtEur(VALEUR_ENVELOPPE)} — sans exception.`}>
      <table className="t">
        <tbody>
          {COUPURES.map((d) => {
            const cle = BILLET_KEY(d)
            const n = Number(q[cle]) || 0
            return (
              <tr key={cle}>
                <td><span className="mo">{d} €</span></td>
                <td style={{ width: 96 }}>
                  <Montant valeur={n} fige={false} largeur={80} entier
                           etiquette={`Nombre de billets de ${d} €`}
                           changer={(val) => setQ((x) => ({ ...x, [cle]: val }))} />
                </td>
                <td className="n" style={{ color: n > 0 ? 'var(--ink)' : 'var(--ink4)' }}>
                  {fmtEur(n * d)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="slot" style={{ borderTop: '1px solid var(--line2)' }}>
        <span className="nm">Total composé</span>
        <span className="sp" />
        <span className="mo" style={{ fontSize: 15, color: v.ok ? 'var(--ok)' : 'var(--stop)' }}>
          {fmtEur(total)}
        </span>
      </div>

      <Notice c={v.ok ? 'ok' : 'stop'}>{v.message}</Notice>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button className="btn" onClick={fermer}>Annuler</button>
        <button className="btn ok" disabled={!v.ok} onClick={() => valider(detail)}>
          Ajouter l’enveloppe
        </button>
      </div>
    </Modale>
  )
}

// ── Saisie d'un montant ───────────────────────────────────────────────────

/**
 * Le contenu se sélectionne au focus : le geste du poste est de remplacer, pas
 * d'insérer. `vide` autorise l'absence — un montant ORIKA non saisi n'est pas
 * un montant nul, et afficher « 0,00 » à sa place inventerait un écart.
 */
function Montant({ valeur, changer, fige, largeur, entier, etiquette, vide }: {
  valeur: number | null
  changer: (v: number) => void
  fige: boolean
  largeur: number
  entier?: boolean
  etiquette: string
  vide?: boolean
}) {
  const [texte, setTexte] = useState<string | null>(null)
  const affiche = texte ?? (valeur === null ? '' : String(valeur))
  return (
    <input
      className="fld mo" inputMode="decimal" aria-label={etiquette} disabled={fige}
      style={{ width: largeur, textAlign: 'right', padding: '5px 7px' }}
      placeholder={vide ? '—' : '0'}
      value={affiche}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => {
        const brut = e.currentTarget.value.replace(',', '.')
        setTexte(e.currentTarget.value)
        if (brut.trim() === '') { changer(0); return }
        const n = Number(brut)
        if (!Number.isFinite(n) || n < 0) return
        changer(entier ? Math.round(n) : +n.toFixed(2))
      }}
      onBlur={() => setTexte(null)}
    />
  )
}
